// Base
import { useEffect, useRef } from "react";

// Store
import { useTemplateStore } from "@/zustand/templateStore";
import { useProductData } from "@/zustand/productStore";
import { useCartStore } from "@/zustand/cartStore";

// Assets
import googlePay from "@/assets/googlePay.svg";

// Google Pay Data Type
export interface GooglePayCustomerAddress {
  address1: string;
  address2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

export interface GooglePayCustomer {
  full_name: string;
  email?: string;
  phone?: string;
  address: GooglePayCustomerAddress;
}

export interface GooglePayData {
  google_pay_token: string;
  customer: GooglePayCustomer;
}

interface GooglePayButtonProps {
  height?: number;
  className?: string;
  onGooglePaySubmit?: (
    data: GooglePayData,
    options?: { signal: AbortSignal },
  ) => Promise<void>;
}

const GooglePayButton = ({
  height,
  onGooglePaySubmit,
  className,
}: GooglePayButtonProps) => {
  const btnWrapRef = useRef<HTMLDivElement>(null);
  const handlePaymentMethodChange = useTemplateStore(
    (s) => s.handlePaymentMethodChange,
  );

  const isGooglePayAvailable = useTemplateStore((s) => s.isGooglePayAvailable);

  const { currency_code, country_code, paymentsConfig } = useProductData();
  const { cartTotal } = useCartStore();

  const {
    gpayGateway,
    gpayGatewayMerchant,
    gpayMerchantId,
    dynamicDescriptor,
  } = paymentsConfig || {};

  useEffect(() => {
    if (!isGooglePayAvailable) return;

    const initGooglePay = () => {
      if (!window.google || !window.google.payments) {
        console.error("GooglePayButton: Google Pay API not loaded");
        return;
      }

      const client = new window.google.payments.api.PaymentsClient({
        environment: "PRODUCTION",
      });

      const baseCardPaymentMethod = {
        type: "CARD",
        parameters: {
          allowedAuthMethods: ["PAN_ONLY", "CRYPTOGRAM_3DS"],
          allowedCardNetworks: ["VISA", "MASTERCARD", "AMEX", "DISCOVER"],
          billingAddressRequired: false,
        },
        tokenizationSpecification: {
          type: "PAYMENT_GATEWAY",
          parameters: {
            gateway: gpayGateway,
            gatewayMerchantId: gpayGatewayMerchant,
          },
        },
      };

      window.googlePayConfig = { client, baseCardPaymentMethod };
    };

    if (window.google && window.google.payments) {
      initGooglePay();
    } else {
      const script = document.createElement("script");
      script.src = "https://pay.google.com/gp/p/js/pay.js";
      script.async = true;
      script.onload = () => {
        // Small delay to ensure Google Pay is fully loaded
        setTimeout(initGooglePay, 100);
      };
      document.head.appendChild(script);
    }
  }, [isGooglePayAvailable, gpayGateway, gpayGatewayMerchant]);

  const onGooglePayClick = async () => {
    const config = window.googlePayConfig;
    if (!config) {
      console.error("Google Pay client not initialized");
      return;
    }

    const { client, baseCardPaymentMethod } = config;

    handlePaymentMethodChange("googlepay");

    const paymentDataRequest = {
      apiVersion: 2,
      apiVersionMinor: 0,
      allowedPaymentMethods: [baseCardPaymentMethod],
      merchantInfo: {
        merchantName: dynamicDescriptor,
        merchantId: gpayMerchantId,
      },
      transactionInfo: {
        totalPriceStatus: "FINAL",
        totalPrice: cartTotal || "0.00",
        currencyCode: currency_code || "USD",
        countryCode: country_code || "US",
      },
      emailRequired: true,
      shippingAddressRequired: true,
      shippingAddressParameters: {
        phoneNumberRequired: true,
      },
    };

    try {
      const paymentData = await client.loadPaymentData(paymentDataRequest);

      const token = paymentData?.paymentMethodData?.tokenizationData?.token;
      const email = paymentData?.email;
      const addr = paymentData?.shippingAddress || {};
      const phone = addr?.phoneNumber || "";
      const name = addr?.name || "";

      const [firstName = "", ...restName] = name.trim().split(/\s+/);
      const lastName = restName.join(" ");

      const googlePayData = {
        google_pay_token: token,
        customer: {
          full_name: name,
          first_name: firstName,
          last_name: lastName,
          phone,
          email,
          address: addr?.address1 || addr?.addressLine1 || "",
          address2: addr?.address2 || addr?.addressLine2 || "",
          city: addr?.locality || "",
          state: addr?.administrativeArea || "",
          zip: addr?.postalCode || "",
          country: addr?.countryCode || "",
        },
      };

      if (onGooglePaySubmit) {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 30000);

        try {
          await onGooglePaySubmit(googlePayData, { signal: controller.signal });
          clearTimeout(timeout);
        } catch (err) {
          clearTimeout(timeout);
          throw err;
        }
      }
    } catch (err: unknown) {
      console.error("GooglePayButton: Error during payment processing:", err);
      if ((err as any)?.statusCode !== "CANCELED") {
        alert("Google Pay processing failed");
      } else {
        console.log("GooglePayButton: Payment was cancelled by user");
      }
    }
  };

  if (!isGooglePayAvailable) {
    return null;
  }

  return (
    <div
      ref={btnWrapRef}
      onClick={onGooglePayClick}
      className={`cursor-pointer ${className}`}
    >
      <img
        src={googlePay}
        alt="Google Pay"
        className={`mx-auto md:max-w-none ${
          height ? `h-[${height}px]` : "h-8"
        }`}
      />
    </div>
  );
};

export default GooglePayButton;
